import {
  SelectData,
  Select as MuiRffSelect,
  TextField as MuiRffTextField,
  TextFieldProps as MuiRffTextFieldProps,
  SelectProps as MuiRffSelectProps,
} from "mui-rff";
import { ReactElement, useMemo } from "react";
import { MenuItem } from "@mui/material";

import * as Validations from "./validation-helpers";
import * as Styled from "./styles";

export type SelectItem = SelectData & {
  group?: string;
};

export type SelectProps = MuiRffSelectProps & {
  options: Array<SelectItem>;
  required?: boolean;
  initialValue?: unknown;
  parse?: (value: unknown) => unknown;
  group?: boolean;
};

export function Select({
  initialValue,
  fieldProps,
  required,
  options,
  sx,
  parse,
  group,
  ...passed
}: SelectProps) {
  const groupedOptions = useMemo(() => {
    if (!group) return options;

    return options.toSorted((a, b) => {
      if (!a.group) return 1;
      if (!b.group) return 0;

      if (a.group < b.group) {
        return -1;
      } else if (a.group > b.group) {
        return 1;
      }
      return 0;
    });
  }, [options, group]);

  const optionsAsNodes = useMemo(() => {
    const reducedOptions: ReactElement[] = [];
    groupedOptions.forEach((option, index) => {
      if (
        group &&
        option.group &&
        !reducedOptions.some((el: ReactElement) => el.key === option.group)
      ) {
        reducedOptions.push(
          <Styled.SelectListHeader key={option.group} disableSticky>
            {option.group}
          </Styled.SelectListHeader>,
        );
      }
      reducedOptions.push(
        <MenuItem
          key={Array.isArray(option.value) ? index : (option.value ?? index)}
          value={option.value}
        >
          {option.label}
        </MenuItem>,
      );
    });
    return reducedOptions;
  }, [groupedOptions, group]);

  return (
    <MuiRffSelect
      formControlProps={{
        sx: {
          mt: 2,
          ...sx,
        },
      }}
      fieldProps={{
        ...fieldProps,
        parse,
        initialValue,
        validate: required ? Validations.required : undefined,
      }}
      variant="standard"
      {...passed}
    >
      {optionsAsNodes}
    </MuiRffSelect>
  );
}

type TextFieldProps = MuiRffTextFieldProps & {
  required?: boolean;
  initialValue?: unknown;
};

export function TextField({
  initialValue,
  required,
  fieldProps,
  variant = "standard",
  ...passed
}: TextFieldProps) {
  return (
    <MuiRffTextField
      sx={{ mt: 2 }}
      fieldProps={{
        initialValue,
        validate: required ? Validations.required : undefined,
        ...fieldProps,
      }}
      variant={variant}
      {...passed}
    />
  );
}
